import { Box, Checkbox, FormControlLabel } from "@mui/material";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  closestCorners,
  pointerWithin,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import React, { useEffect, useState } from "react";
import BackEndColumn from "./backend-column/column";
import FileColumn from "./front-column/column";
import {
  applyColumnDrag,
  buildInitialColumns,
  createFileColumnList,
  getFieldName,
  getSpreadsheetColumnName
} from "../utils";

function importCollisionDetection(args) {
  const pointerHits = pointerWithin(args);
  if (pointerHits.length > 0) {
    return pointerHits;
  }
  return closestCorners(args);
}

const MappingList = ({
  setFileRowsAmount,
  fileColumnNames,
  setIsFirstRowColumnated,
  isFirstRowColumnated,
  setColumnsToSend,
  backendColumnNames,
  fileDataAmount,
  columnsToSend
}) => {
  const [columns, setColumns] = useState(() =>
    buildInitialColumns(fileColumnNames, backendColumnNames, columnsToSend)
  );
  const [activeField, setActiveField] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    })
  );

  useEffect(() => {
    setColumnsToSend(columns);
  }, [columns, setColumnsToSend]);

  useEffect(() => {
    if (!backendColumnNames?.length) return;
    setColumns((prev) => {
      if (prev.BACKEND.list.length > 0) return prev;
      const mappedTokens = new Set(prev.FILE.list.map((col) => col.token).filter(Boolean));
      return {
        ...prev,
        BACKEND: {
          ...prev.BACKEND,
          list: backendColumnNames
            .filter((name) => !mappedTokens.has(name))
            .map((columnName) => ({ id: `field:${columnName}`, columnName }))
        }
      };
    });
  }, [backendColumnNames]);

  const onDragStart = ({ active }) => {
    setActiveField(getFieldName(active.id));
  };

  const onDragCancel = () => {
    setActiveField(null);
  };

  const onDragEnd = ({ active, over }) => {
    setActiveField(null);
    setColumns((prev) => {
      const next = applyColumnDrag({
        fileList: prev.FILE.list,
        backendList: prev.BACKEND.list,
        activeId: active?.id,
        overId: over?.id
      });
      return {
        ...prev,
        FILE: { ...prev.FILE, list: next.fileList },
        BACKEND: { ...prev.BACKEND, list: next.backendList }
      };
    });
  };

  const updateFileColumnNames = (e) => {
    const checked = e.target.checked;
    const nextNames = checked
      ? fileColumnNames
      : (fileColumnNames || []).map((_, index) => getSpreadsheetColumnName(index + 1));

    setColumns((prev) => ({
      ...prev,
      FILE: {
        ...prev.FILE,
        list: createFileColumnList(nextNames, prev.FILE.list)
      }
    }));

    if (fileDataAmount > 0 && !checked) {
      setFileRowsAmount((prev) => prev + 1);
    }
    if (fileDataAmount > 0 && checked) {
      setFileRowsAmount((prev) => prev - 1);
    }
    setIsFirstRowColumnated(checked);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={importCollisionDetection}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={onDragStart}
      onDragCancel={onDragCancel}
      onDragEnd={onDragEnd}
    >
      <Box>
        <FormControlLabel
          label="First Row Contains Column Names"
          control={<Checkbox checked={isFirstRowColumnated} onChange={updateFileColumnNames} />}
        />
        <Box display="flex" style={{ overflow: "hidden", height: "300px" }}>
          <FileColumn column={columns.FILE} />
          <BackEndColumn column={columns.BACKEND} />
        </Box>
      </Box>
      <DragOverlay dropAnimation={null} style={{ zIndex: 1700 }} zIndex={1700}>
        {activeField ? (
          <Box
            sx={{
              px: 1,
              py: 0.5,
              bgcolor: "background.paper",
              border: "1px solid lightgrey",
              borderRadius: 1,
              boxShadow: 2,
              cursor: "grabbing"
            }}
          >
            {activeField}
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default MappingList;
